// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useMemo } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { useAccountMenuPage } from './hooks';

import { ViewerItem } from 'presentations/api/schema/user';
import DashboardSettingPageTemplate from 'presentations/views/components/templates/DashboardSettingPageTemplate';
import FormContentHeader from 'presentations/views/components/molecules/FormContentHeader';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  attention,
  black,
  gray01,
  gray02,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import { formatToDate, getDateTime } from 'infras/datetime';
import Icon from 'presentations/views/components/atoms/Icon';
import PlanIcon from 'presentations/views/components/atoms/PlanIcon';
import Link from 'presentations/views/components/atoms/Link';
import Button from 'presentations/views/components/atoms/Button';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Headline from 'presentations/views/components/atoms/Headline';
import {
  accountSettingProfilePath,
  adminPaymentPath,
  adminSettingPath,
  calendarIntegrationsPath,
  hostEventTypeSettingPath,
  integrationListPath,
  launchAppPath,
  loginPath,
  logoutPath,
  pricingPath,
  newsPath,
  serviceListPath,
  webPath,
  termListPath,
  adminUserListPath,
} from 'presentations/routes/path';
import { Role } from 'domains/models/team_user/role';
import { isiOSApp as isiOSAppFunc } from 'presentations/views/helpers/electron';
import { getPersistentItem } from 'presentations/views/helpers/storage';
import LanguageSelect from 'presentations/views/components/molecules/LanguageSelect';
import Select from 'presentations/views/components/atoms/Select';
import { UserNotificationTokenKey } from 'infras/firebase/client/webpush';
import { developerCenterPath } from 'presentations/routes/path/developer';
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';

export interface Props {
  viewer?: ViewerItem;
}

interface GroupItem {
  title: string;
  icon: string;
  subGroups?: SubGroupItem[];
  items?: MenuItem[];
}

interface SubGroupItem {
  title: string;
  items: MenuItem[];
}

interface MenuItem {
  title: string;
  description?: string;
  link?: string;
  onClick?: Function;
  target?: string;
  items?: MenuItem[];
}

// リソース記事に新着アイコンをつける日数
const DAYS_TO_PUT_NEW_IN_RESOURCE = 7;

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const router = useRouter();

  const { state, actions } = useAccountMenuPage({ viewer: props.viewer });

  const isTablet = isMediaMax('tablet');

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isiOSApp = isiOSAppFunc(userAgent);
  const isAdmin = !!state.viewer && state.viewer.role <= Role.Admin;

  const cachedUserNotificationToken: string | undefined = useMemo(() => {
    return getPersistentItem(UserNotificationTokenKey);
  }, []);

  const accountMenuOptions = [
    {
      icon: 'account-lock',
      label: t('Login'),
    },
  ];

  const groups: GroupItem[] = useMemo(() => {
    const target = state.isElectron ? '_blank' : undefined;

    const results: GroupItem[] = [
      {
        title: t('personal group'),
        icon: 'account',
        items: [
          {
            title: t('integration title'),
            description: t('integration description'),
            link: integrationListPath(),
          },
          {
            title: t('account title'),
            description: t('account description'),
            link: accountSettingProfilePath(),
            target,
          },
        ],
      },
      {
        title: t('calendar settings'),
        icon: 'calendar-month',
        items: [
          {
            title: t('eventType title'),
            description: t('eventType description'),
            link: hostEventTypeSettingPath({
              from: encodeURIComponent(router.asPath),
            }),
          },
          {
            title: t('calendar title'),
            description: t('calendar description'),
            link: calendarIntegrationsPath(),
          },
        ],
      },
    ];
    if (state.viewer && state.viewer.role <= Role.Member) {
    }
    if (state.viewer && state.viewer.role <= Role.Member) {
    }

    if (isAdmin) {
      results.push({
        title: t('team group'),
        icon: 'account-group',
        subGroups: [
          {
            title: t('feature settings'),
            items: [
              {
                title: t('service title'),
                description: t('service description'),
                link: serviceListPath(),
              },
              {
                title: t('terms title'),
                description: t('terms description'),
                link: termListPath(),
              },
            ],
          },
        ],
        items: [
          {
            title: t('team title'),
            description: t('team description'),
            link: adminSettingPath(),
            target,
          },
          {
            title: t('member title'),
            description: t('member description'),
            link: adminUserListPath(),
            target,
          },
          {
            title: t('pricing title'),
            description: t('pricing description'),
            link: adminPaymentPath(),
            target,
          },
          {
            title: t('plan title'),
            description: t('plan description'),
            link: pricingPath(),
            onClick: (e: any) => {
              if (isiOSApp) {
                e.preventDefault();
                if (
                  typeof window.webkit?.messageHandlers?.requestToOpenBrowser
                    ?.postMessage === 'function'
                ) {
                  window.webkit.messageHandlers.requestToOpenBrowser.postMessage(
                    webPath(
                      loginPath({
                        to: pricingPath(),
                        redirectIfLoggedIn: true,
                      }),
                      router.locale,
                    ),
                  );
                }
              }
            },
            target,
          },
        ],
      });
    }

    results.push({
      title: t('Developer'),
      icon: 'tools',
      items: [
        {
          title: t('Developer center'),
          // description: t('Developer center description'),
          link: developerCenterPath(),
          target: '_blank',
        },
      ],
    });

    return results;
  }, [state.viewer, state.isElectron, isiOSApp]);

  const renderMenuItem = (item: MenuItem) => {
    return (
      <Menu key={item.link}>
        <MenuLink href={item.link} target={item.target} onClick={item.onClick}>
          <MenuTitle>
            <MenuIcon name="chevron-right" color="primary" />
            {item.title}
          </MenuTitle>
          {item.description && (
            <MenuDescription>{item.description}</MenuDescription>
          )}
        </MenuLink>
      </Menu>
    );
  };

  return (
    <DashboardSettingPageTemplate
      isLoading={false}
      contentHeader={
        <FormContentHeader
          icon="settings"
          title={t('title')}
          description={t('description')}
        />
      }
      viewer={state.viewer!}
      // showContentHeaderInElectron
      // isFooterHidden
      content={
        state.viewer ? (
          <>
            <AccountContainer>
              <ProfileContainer>
                <ProfileImage src={state.viewer.profileImage}>
                  {state.viewer.name ? state.viewer.name.charAt(0) : ''}
                </ProfileImage>
                <ProfileInfo>
                  <PlanIcon
                    planId={state.viewer.planId}
                    planVersion={state.viewer.planVersion}
                  />
                  <ProfileName>{state.viewer.name}</ProfileName>
                  <ProfileButtonContainer>
                    <ProfileEditButton
                      size="small"
                      variant="outlined"
                      href={accountSettingProfilePath()}
                    >
                      {t('Edit Profile')}
                    </ProfileEditButton>
                    <ProfileMoreButton
                      size="small"
                      variant="outlined"
                      onClick={actions.onClickAccountMenu}
                    >
                      <ProfileMoreIcon name="dots-vertical" />
                    </ProfileMoreButton>
                  </ProfileButtonContainer>
                </ProfileInfo>
              </ProfileContainer>
              {isAdmin && (
                <TeamContainer>
                  <TeamProfileContainer>
                    <TeamNameContainer>
                      <TeamImage
                        src={state.viewer.team?.profileImage || ''}
                        alt={state.viewer.team?.name || ''}
                      />
                      <TeamName>{state.viewer.team?.name}</TeamName>
                    </TeamNameContainer>
                    <TeamButtonContainer>
                      <TeamSettingButton
                        size="small"
                        variant="outlined"
                        href={adminSettingPath()}
                      >
                        {t('Team Settings')}
                      </TeamSettingButton>
                      <TeamAccountButton
                        size="small"
                        variant="outlined"
                        href={adminUserListPath()}
                      >
                        <TeamAccountIcon name="account-cog" />
                      </TeamAccountButton>
                    </TeamButtonContainer>
                  </TeamProfileContainer>
                  <SubscriptionContainer>
                    <SubscriptionInfo>
                      <SubscriptionInfoIcon
                        color="primary"
                        name="chevron-right"
                      />
                      <SubscriptionInfoText href={adminPaymentPath()}>
                        {t('confirm subscription')}
                      </SubscriptionInfoText>
                    </SubscriptionInfo>
                    <InviteButton color="primary" href={adminUserListPath()}>
                      {t('Add members')}
                    </InviteButton>
                  </SubscriptionContainer>
                </TeamContainer>
              )}
            </AccountContainer>
            <Container>
              <Main>
                <GroupContainer>
                  {groups.map((group) => (
                    <Group key={group.title}>
                      <GroupTitle>
                        <GroupIcon name={group.icon} color="primary" />
                        {group.title}
                      </GroupTitle>
                      {group.items && (
                        <MenuList>
                          {group.items.map((item) => renderMenuItem(item))}
                        </MenuList>
                      )}
                      {group.subGroups &&
                        group.subGroups.map((subGroup) => (
                          <>
                            <SubGroupTitle>{subGroup.title}</SubGroupTitle>
                            <MenuList>
                              {subGroup.items.map((item) =>
                                renderMenuItem(item),
                              )}
                            </MenuList>
                          </>
                        ))}
                    </Group>
                  ))}
                </GroupContainer>
                <GroupContainer>
                  <Group>
                    <GroupTitle>{t('other group')}</GroupTitle>
                    <MenuList>
                      {/* <Menu>
                        <MenuTitle>
                          <MenuIcon name="chevron-right" color="primary" />
                          {t('Locale')}
                        </MenuTitle>
                        <MenuDescription>
                          <StyledLanguageSelect
                            onChangeLocale={actions.onChangeLocale}
                          />
                        </MenuDescription>
                      </Menu> */}
                      <Menu>
                        <MenuTitle>
                          <MenuIcon name="chevron-right" color="primary" />
                          {t('Appearance')}
                        </MenuTitle>
                        <MenuDescription>
                          <StyledSelect
                            onChange={actions.onChangeAppearance}
                            options={state.appearanceOptions}
                            size="small"
                            value={state.appearance}
                          />
                        </MenuDescription>
                      </Menu>
                    </MenuList>
                  </Group>
                </GroupContainer>
                <GroupContainer>
                  <Group>
                    <MenuList isTwoColumnInMobile>
                      {!state.isElectron && !isTablet && (
                        <Menu>
                          <MenuLink href={launchAppPath()} target="_blank">
                            <MenuTitle>
                              <MenuIcon name="chevron-right" color="primary" />
                              {t('Open app')}
                            </MenuTitle>
                          </MenuLink>
                        </Menu>
                      )}
                      <Menu>
                        <MenuLink
                          href={logoutPath({
                            userNotificationToken: cachedUserNotificationToken,
                          })}
                          withoutNextLink
                        >
                          <MenuTitle>
                            <MenuIcon name="chevron-right" color="primary" />
                            {t('Logout')}
                          </MenuTitle>
                        </MenuLink>
                      </Menu>
                    </MenuList>
                  </Group>
                </GroupContainer>
              </Main>
              <Sub>
                <BoxHeader>
                  <BoxHeadline variant="h2">{t('Update')}</BoxHeadline>
                </BoxHeader>
                <ResourceList>
                  {state.blogs.map((blog) => (
                    <Resource key={blog.id}>
                      <ResourceTitle target="jicoo" href={newsPath(blog.slug)}>
                        {blog.title}
                      </ResourceTitle>
                      <ResourceDate>
                        {formatToDate(blog.createdAt)}
                      </ResourceDate>
                      {getDateTime(blog.createdAt).diffNow('day').days >
                        -DAYS_TO_PUT_NEW_IN_RESOURCE && <ResourceNewIcon />}
                    </Resource>
                  ))}
                  {/* <ViewAllLink
                    href={blogCategoryPath({ category: 'news' })}
                    target="_blank"
                  >
                    <ArrowIcon name="chevron-right" color="primary" />
                    <ViewAllLabel>{t('View all')}</ViewAllLabel>
                  </ViewAllLink> */}
                </ResourceList>
              </Sub>
            </Container>
            <FloatSettingMenu
              onCloseMenu={actions.onCloseAccountMenu}
              onSelectMenu={actions.onSelectAccountMenu}
              anchor={state.accountMenuAnchor}
              options={accountMenuOptions}
            />
          </>
        ) : null
      }
    />
  );
}

const AccountContainer = styled.div`
  margin-bottom: ${spacing(32)};
  padding: ${spacing(32)};
  background: ${white};
  border-radius: ${borderRadius(16)};
  display: flex;
  justify-content: space-between;

  ${mediaMax.tablet} {
    padding: ${spacing(16)};
    margin-bottom: ${spacing(20)};
    display: block;
  }
`;
const ProfileContainer = styled.div`
  display: flex;
  ${mediaMax.tablet} {
  }
`;
const ProfileImage = styled(Avatar)`
  width: 80px;
  height: 80px;
  margin-right: ${spacing(20)};
`;
const ProfileInfo = styled.div``;
const ProfileName = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;
const ProfileEditButton = styled(Button)`
  margin-right: ${spacing(8)};
  border: 1px solid ${gray04};
`;
const ProfileMoreButton = styled(Button)`
  border: 1px solid ${gray04};
  min-width: 32px;
`;
const ProfileButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;
const TeamContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  border-left: 1px solid ${gray04};
  padding-left: ${spacing(28)};
  margin-left: ${spacing(28)};
  ${mediaMax.tablet} {
    border-left: 0;
    display: block;
    padding-left: 0;
    margin-left: 0;
    margin-top: ${spacing(16)};
    border-top: 1px solid ${gray04};
    padding-top: ${spacing(16)};
  }
`;
const TeamProfileContainer = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
`;
const TeamNameContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(12)};
`;
const TeamImage = styled(Avatar)`
  width: 24px;
  height: 24px;
  margin-right: ${spacing(8)};
`;
const TeamName = styled.div``;
const TeamButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;
const TeamSettingButton = styled(Button)`
  margin-right: ${spacing(8)};
  border: 1px solid ${gray04};
`;
const TeamAccountButton = styled(Button)`
  border: 1px solid ${gray04};
  min-width: 32px;
`;
const TeamAccountIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
`;
const SubscriptionContainer = styled.div`
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  padding: ${spacing(24)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${mediaMax.tablet} {
    padding: ${spacing(16)};
  }
`;
const SubscriptionInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SubscriptionInfoIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(2)};
`;
const SubscriptionInfoText = styled(Link)`
  color: ${black};
`;
const InviteButton = styled(Button)`
  margin-top: ${spacing(8)};
`;

const Container = styled.div`
  display: flex;
  align-items: flex-start;

  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;

const Main = styled.div`
  flex: 1;
  margin-right: ${spacing(40)};

  ${mediaMax.tablet} {
    width: 100%;
    margin-right: 0;
  }
`;
const GroupContainer = styled.div`
  padding: ${spacing(32)};
  margin-bottom: ${spacing(24)};
  background: ${white};
  border-radius: ${borderRadius(16)};

  ${mediaMax.tablet} {
    padding: ${spacing(16)};
    margin-bottom: ${spacing(20)};
  }
`;
const Group = styled.div`
  padding: ${spacing(24)} 0;
  border-bottom: 1px solid ${gray04};

  :first-of-type {
    padding-top: 0;
  }
  :last-of-type {
    padding-bottom: 0;
    border-bottom: none;
  }

  ${mediaMax.tablet} {
    padding: 0 0 ${spacing(20)} 0;
    border-bottom: none;
  }
`;
const GroupTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  color: ${black};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(12)};
    line-height: 16px;
  }
`;
const SubGroupTitle = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing(16)} 0;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(12)};
    line-height: 16px;
  }
`;
const GroupIcon = styled(Icon)`
  width: 24px !important;
  height: 24px !important;
  margin-right: ${spacing(8)};

  ${mediaMax.tablet} {
    width: 16px !important;
    height: 16px !important;
    margin-right: ${spacing(4)};
  }
`;
const MenuList = styled.div<{ isTwoColumnInMobile?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  row-gap: 24px;

  ${mediaMax.tablet} {
    grid-template-columns: ${({ isTwoColumnInMobile }) =>
      isTwoColumnInMobile ? '1fr 1fr' : '1fr'};
    column-gap: 12px;
    row-gap: 12px;
  }
`;
const MenuTitle = styled.div`
  display: flex;
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;
const MenuIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;

  ${mediaMax.tablet} {
    width: 18px !important;
    height: 18px !important;
  }
`;
const Menu = styled.div<{ isHover?: boolean }>`
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(12)};
  padding: ${spacing(16)};
  :hover {
    border-color: ${(props) => {
      if (props.isHover) return primary;
    }};

    ${MenuTitle} {
      color: ${(props) => {
        if (props.isHover) return primary;
      }};
    }
  }
`;
const MenuLink = styled(Link)`
  display: block;
`;
const MenuDescription = styled.div`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(13)};
  color: ${gray01};
`;

const Sub = styled.div`
  width: 290px;
  height: 100%;

  ${mediaMax.tablet} {
    width: 100%;
    padding-top: ${spacing(20)};
    border-top: 1px solid ${gray04};
  }
`;
const BoxHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 ${spacing(24)};

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(12)};
  }
`;
const BoxHeadline = styled(Headline)`
  font-size: ${fontSize(20)};
`;
const ResourceList = styled.div`
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: 0;
  }
`;
const Resource = styled.div`
  position: relative;
  margin-bottom: ${spacing(16)};
  padding: ${spacing(20)} ${spacing(16)};
  background-color: ${white};
  border-radius: ${borderRadius(12)};

  ${mediaMax.tablet} {
    padding: ${spacing(16)};
    margin-bottom: ${spacing(12)};
  }
`;
const ResourceTitle = styled(Link)`
  display: block;
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;
const ResourceDate = styled.div`
  color: ${gray02};

  ${mediaMax.tablet} {
    margin-top: ${spacing(4)};
  }
`;
const ResourceNewIcon = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  position: absolute;
  right: 12px;
  top: 12px;
  background-color: ${primary};
`;
const StyledLanguageSelect = styled(LanguageSelect)`
  width: 100%;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

export const LeaveLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-top: ${spacing(24)};
  color: ${attention};
  font-weight: ${fontWeight('bold')};
`;

const ProfileMoreIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
`;
